import React from "react";

// The small Markdown subset Tiger X AI is allowed to write: paragraphs, **bold**,
// "1." and "-" lists, [text](url) and bare https:// links, and one pipe table.
// Builds React elements only (never raw HTML), so answer text can't inject markup.
// Safe mid-stream: an unfinished list or table just renders as text until it closes.

const SAFE_URL = /^https?:\/\//i;
const INLINE = /\*\*([^*]+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g;

function link(href, label, key) {
  if (!SAFE_URL.test(href)) return label;
  return (
    <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="ai-md__link">
      {label}
    </a>
  );
}

export function renderInline(text, keyPrefix = "i") {
  const out = [];
  let last = 0;
  let m;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyPrefix}-${m.index}`;
    if (m[1] !== undefined) out.push(<strong key={key}>{m[1]}</strong>);
    else if (m[2] !== undefined) out.push(link(m[3], m[2], key));
    else out.push(link(m[4], m[4].replace(/^https?:\/\/(www\.)?/i, ""), key));
    last = INLINE.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const ORDERED = /^\s*(\d+)[.)]\s+(.*)$/;
const BULLET = /^\s*[-*•]\s+(.*)$/;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

const cells = (line) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());

export default function renderMarkdown(source) {
  const lines = String(source || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i++;
      continue;
    }

    // Table: header row, separator row, then body rows.
    if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1])) {
      const header = cells(line);
      const rows = [];
      i += 2;
      while (i < lines.length && TABLE_ROW.test(lines[i])) rows.push(cells(lines[i++]));
      const key = `t${blocks.length}`;
      blocks.push(
        <div key={key} className="ai-md__table-wrap">
          <table className="ai-md__table">
            <thead>
              <tr>
                {header.map((c, ci) => (
                  <th key={ci}>{renderInline(c, `${key}h${ci}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {header.map((_, ci) => (
                    <td key={ci}>{renderInline(r[ci] ?? "", `${key}r${ri}c${ci}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    const ordered = ORDERED.test(line);
    if (ordered || BULLET.test(line)) {
      const pattern = ordered ? ORDERED : BULLET;
      const start = ordered ? Number(line.match(ORDERED)[1]) : undefined;
      const items = [];
      while (i < lines.length && pattern.test(lines[i])) {
        const m = lines[i].match(pattern);
        items.push(ordered ? m[2] : m[1]);
        i++;
        // A wrapped continuation line belongs to the previous item.
        while (
          i < lines.length &&
          lines[i].trim() &&
          /^\s{2,}\S/.test(lines[i]) &&
          !ORDERED.test(lines[i]) &&
          !BULLET.test(lines[i])
        ) {
          items[items.length - 1] += ` ${lines[i].trim()}`;
          i++;
        }
      }
      const key = `l${blocks.length}`;
      const ListTag = ordered ? "ol" : "ul";
      blocks.push(
        <ListTag key={key} className="ai-md__list" start={start && start !== 1 ? start : undefined}>
          {items.map((item, ii) => (
            <li key={ii}>{renderInline(item, `${key}-${ii}`)}</li>
          ))}
        </ListTag>,
      );
      continue;
    }

    // Paragraph: consecutive plain lines, kept as line breaks.
    const para = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !ORDERED.test(lines[i]) &&
      !BULLET.test(lines[i]) &&
      !(TABLE_ROW.test(lines[i]) && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1]))
    ) {
      para.push(lines[i]);
      i++;
    }
    const key = `p${blocks.length}`;
    blocks.push(
      <p key={key} className="ai-md__p">
        {para.map((l, li) => (
          <React.Fragment key={li}>
            {renderInline(l, `${key}-${li}`)}
            {li < para.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>,
    );
  }

  return blocks;
}

// Plain text for the copy button: links become "text (url)", bold markers dropped.
export function markdownToPlain(source) {
  return String(source || "")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, "$1 ($2)")
    .replace(/\*\*([^*]+?)\*\*/g, "$1");
}
